import React, { useCallback, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { IVisitRequestForm } from '@interfaces/VisitRequest.interface';
import { useTranslate } from '@translations/useTranslate';
import { IFormItemType } from '@interfaces/FormItemType';
import { CustomButton, FormContainer } from '@ui/components/atoms';
import { Form } from 'react-bootstrap';
import { useSubmitVisitRequestForm } from '@services/VisitRequest';
import ArrowIcon from '@assets/svg/arrow.svg';
import { SubFormSection } from '@ui/components/molecules/SubFormSection';
import { ConsentBox } from '@ui/components/atoms/ConsentBox';
import { visitRequestFormValidationScheme } from '@ui/components/organisms/VisitRequestForm/VisitRequestForm.scheme';
import { IMappedVisitRequestResponse } from '@interfaces/VisitRequestLookups.interface';
import PrivacyNotice from '@ui/components/atoms/PrivacyNotice';

export interface VisitRequestFormProps {
  requestId: string;
  isVVIP: boolean;
  requestData: IMappedVisitRequestResponse;
  onRequestSuccess: (successRequestId: string) => void;
  onRequestFailed: () => void;
}
export const VisitRequestForm: React.FC<VisitRequestFormProps> = ({
  requestId,
  isVVIP,
  requestData,
  onRequestSuccess,
  onRequestFailed,
}) => {
  const { t } = useTranslate();
  const [error, setError] = React.useState<string | undefined>();
  const [identityChosen, setIdentityChosen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isPrivacyNoticeChecked, setIsPrivacyNoticeChecked] = useState(false);
  const [validateConsent, setValidateConsent] = useState(false);

  const { mutateAsync: submitRequest, isLoading } = useSubmitVisitRequestForm();

  const submit = useCallback((values) => {
    setError('');
    const removedEmptyValues: IVisitRequestForm = Object.fromEntries(
      Object.entries(values).map(([key, value]) => {
        return [key, value === '' ? undefined : value];
      })
    ) as unknown as IVisitRequestForm;

    const formData = new FormData();
    Object.entries(removedEmptyValues).forEach(([key, value]) => {
      if (value) {
        formData.append(key, value ?? '');
      }
    });
    formData.append('isRejected', 'false');
    submitRequest(formData, {
      onError: (error) => {
        const errorData = error?.response?.data as { errorMessage?: string };
        setError(errorData?.errorMessage ?? t('global.error'));
        onRequestFailed();
      },
    })
      .then((id) => {
        onRequestSuccess(id);
        //navigate to next page
      })
      .catch(() => {
        //Ignore the exception
      });
  }, []);

  const visitRequestFormScheme = useMemo(
    () => visitRequestFormValidationScheme(t, requestData.isVVIP),
    [t]
  );
  const formik = useFormik<IVisitRequestForm>({
    initialValues: {
      isRejected: false,
      requestId: requestId,
      identity: '',
      identityValue: '',
    },
    onSubmit: (values) => {
      submit(values);
    },
    validationSchema: visitRequestFormScheme,
    validateOnChange: false,
    validateOnBlur: true,
  });

  const userDetailsFormItems: IFormItemType[] = useMemo(() => {
    const formItems: IFormItemType[] = [];
    if (!isVVIP) {
      formItems.push({
        id: 'identity',
        label: t('visit.request.identity.label'),
        placeholder: t('visit.request.identity.placeholder'),
        type: 'select',
        required: true,
        options: requestData.identityList,
        value: formik.values.identity,
        isTouched: formik.touched.identity,
        isValid: !formik.errors.identity,
        errorFeedback: formik.errors.identity,
        maxLength: 10,
        onChange: (value) => {
          setIdentityChosen(true);
          formik.setFieldValue('identity', value);
        },
        onBlur: () => formik.setFieldTouched('identity', true),
      });
    }
    if (identityChosen && !isVVIP) {
      formItems.push({
        id: 'identityValue',
        label: t('visit.request.DocumentID.label'),
        placeholder: t('visit.request.DocumentID.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.identityValue,
        isTouched: formik.touched.identityValue,
        isValid: !formik.errors.identityValue,
        errorFeedback: formik.errors.identityValue,
        maxLength: 25,
        onChange: (value) => formik.setFieldValue('identityValue', value),
        onBlur: () => formik.setFieldTouched('identityValue', true),
      });
    }

    return formItems;
  }, [formik, identityChosen, t]);

  return (
    <FormContainer error={error}>
      <SubFormSection items={userDetailsFormItems} />
      <ConsentBox />

      <Form.Group className="form-check">
        <Form.Check
          label=" I acknowledge to TAWAL data policy"
          type="checkbox"
          value="consent-check"
          required={true}
          onChange={(event) => setIsChecked(event.target.checked)}
        />

        {!isChecked && validateConsent ? (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {' '}
            {'please accept the policy'}{' '}
          </div>
        ) : null}
      </Form.Group>

      <PrivacyNotice />

      <Form.Group className="form-check">
        <Form.Check
          label="By clicking here, I acknowledge Tawal collecting and processing my personal data per its Privacy Notice"
          type="checkbox"
          value="privacy-notice-check"
          required={true}
          onChange={(event) => setIsPrivacyNoticeChecked(event.target.checked)}
        />

        {!isPrivacyNoticeChecked && validateConsent ? (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {' '}
            {'please accept the privacy-notice'}{' '}
          </div>
        ) : null}
      </Form.Group>

      <Form.Group className="w-100 pb-3 px-3 d-flex flex-column justify-content-start align-items-start" />
      <div className="w-100 pt-3 pb-5 d-flex justify-content-center align-items-center">
        <CustomButton
          variant="primary"
          text={t('submit')}
          disabled={isLoading || !isChecked}
          suffixIcon={ArrowIcon}
          onClick={() => {
            setValidateConsent(true);
            formik.handleSubmit();
          }}
        />
      </div>
    </FormContainer>
  );
};
